import {
  ArrowDownTrayIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PrinterIcon,
} from '@heroicons/react/20/solid';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FourOhFour } from '../../components/404';
import {
  IconButton,
  PrimaryButton,
  SecondaryButton,
  SecondaryLinkButton,
} from '../../components/buttons';
import { Modal } from '../../components/modal';
import { PrintContainer } from '../../components/print-container';
import { AlertContext } from '../../contexts/alert-context';
import { orderDeliveredReps, orderDeliveryState } from '../../data/order';
import { casesOf, unitsOf } from '../../data/order-item';
import { bounceReasonForSubType } from '../../data/representative';
import {
  useCreateOrder,
  useDownloadOrder,
  useGoToOrderCleanup,
  useOrders,
} from '../../hooks/orders';
import { useVendor } from '../../hooks/vendors';
import { orderSummaryRoute, storeLayout } from '../../route-apis';
import { classifyOrderConflict } from '../../services/orders';
import { PurchaseOrder } from './purchase-order';

export function OrderSummary() {
  const { store } = storeLayout.useRouteContext();
  const { orderId } = orderSummaryRoute.useParams();
  const { orders } = useOrders();
  const order = useMemo(() => orders.find((order) => order.id === orderId), [orders, orderId]);
  const { vendor } = useVendor(order?.vendor_id);
  const createOrder = useCreateOrder();
  const goToCleanup = useGoToOrderCleanup();
  const navigate = useNavigate();
  const location = useLocation();
  const { addErrorAlert } = useContext(AlertContext);

  const downloadOrder = useDownloadOrder();
  const hasTriggeredDownload = useRef(false);

  const [duplicateConfirmationIsOpen, setDuplicateConfirmationIsOpen] = useState(false);

  const [printing, setPrinting] = useState(false);

  // Auto-trigger download only when arriving from order submission.
  // Clear location state immediately so a refresh won't re-trigger the download.
  const justSubmitted = location.state.justSubmitted === true;
  useEffect(() => {
    if (justSubmitted) {
      navigate({
        to: '/stores/$storeId/orders/$orderId/summary',
        params: { storeId: store.id, orderId },
        replace: true,
        state: {},
      });
    }
  }, [justSubmitted, navigate, store.id, orderId]);

  useEffect(() => {
    if (justSubmitted && order?.send_method === 'download' && !hasTriggeredDownload.current) {
      hasTriggeredDownload.current = true;
      downloadOrder(store.id, order.id);
    }
  }, [justSubmitted, order, store, downloadOrder]);

  const handleRedownload = useCallback(() => {
    if (order) {
      downloadOrder(store.id, order.id);
    }
  }, [store, order, downloadOrder]);

  const displayOrderItems = useMemo(() => {
    return order?.items.filter((i) => casesOf(i) > 0 || unitsOf(i) > 0) ?? [];
  }, [order]);

  if (!order || !vendor) {
    return <FourOhFour />;
  }

  const downloadInfo = order.download_info;
  const deliveryState = orderDeliveryState(order);
  const isFailed = deliveryState === 'failed';
  const deliveredReps = orderDeliveredReps(order);

  const duplicateOrder = () => {
    createOrder(store.id, {
      vendor_id: vendor.vendor_id,
      options: order.options,
      items: order.items,
    })
      .then((newOrder) => {
        navigate({
          to: '/stores/$storeId/orders/$orderId/edit',
          params: { storeId: store.id, orderId: newOrder.id },
        });
      })
      .catch((error: unknown) => {
        // Duplicating into a vendor that already has an open draft is the same
        // situation the New Order button handles, and it gets the same answer
        // — the cleanup screen, rather than an alert that leaves the user
        // nowhere (ORD-459).
        if (classifyOrderConflict(error) === 'draft-exists') {
          void goToCleanup(store.id, vendor.vendor_id, { refetchOrders: true });

          return;
        }

        addErrorAlert('Error duplicating order', { cause: error });
      });

    setDuplicateConfirmationIsOpen(false);
  };

  return (
    <>
      <div className="flex justify-center">
        <div className="max-w-[1000px]">
          <div className="p-4 grid grid-cols-2">
            <div className="justify-self-start">
              <SecondaryLinkButton to="/stores/$storeId/orders" params={{ storeId: store.id }}>
                Back to Orders
              </SecondaryLinkButton>
            </div>
            <div className="flex flex-row gap-2 justify-self-end">
              <PrimaryButton onClick={() => setDuplicateConfirmationIsOpen(true)}>
                Duplicate Order
              </PrimaryButton>
              <IconButton tooltip="Print order summary" onClick={() => setPrinting(true)}>
                <PrinterIcon className="h-5 w-5" aria-hidden="true" />
              </IconButton>
            </div>
            {deliveryState !== 'clean' && (
              <div
                className={`col-span-2 mt-3 rounded-md border p-3 text-sm ${
                  isFailed
                    ? 'bg-status-danger-soft border-status-danger/30 text-status-danger'
                    : 'bg-status-attention-soft border-status-attention/30 text-status-attention'
                }`}
              >
                <p className="flex items-center gap-2 font-semibold">
                  {isFailed ? (
                    <ExclamationCircleIcon className="h-5 w-5 flex-none" aria-hidden="true" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 flex-none" aria-hidden="true" />
                  )}
                  {isFailed
                    ? `This order couldn't be delivered to ${vendor.name}.`
                    : 'Some recipients did not receive this order.'}
                </p>
                {order.delivery_failures.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {order.delivery_failures.map((failure) => (
                      <li key={`${failure.email}-${failure.feedback_id}`}>
                        <span className="font-medium">{failure.email}</span>:{' '}
                        {bounceReasonForSubType(failure.sub_type)}
                      </li>
                    ))}
                  </ul>
                )}
                {deliveredReps.length > 0 && (
                  <p className="mt-2">
                    Delivered to <span className="font-medium">{deliveredReps.join(', ')}</span>.
                  </p>
                )}
                <p className="mt-2">
                  <Link
                    to="/stores/$storeId/vendors/$vendorId/edit"
                    params={{ storeId: store.id, vendorId: vendor.vendor_id }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    Update Vendor Reps
                  </Link>
                  {isFailed && ', then re-send this order.'}
                </p>
              </div>
            )}
            {order.send_method === 'download' && (
              <div className="col-span-2 mt-3 bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
                {downloadInfo ? (
                  <div className="space-y-3">
                    <p className="font-medium">
                      {justSubmitted
                        ? 'Your order files have been downloaded.'
                        : 'This order was submitted as a downloadable file.'}
                    </p>
                    <ul className="space-y-1">
                      {Array.from({ length: downloadInfo.file_count }, (_, i) => {
                        const maxRows = downloadInfo.max_rows;
                        const start = maxRows > 0 ? i * maxRows + 1 : 1;
                        const end =
                          maxRows > 0
                            ? Math.min((i + 1) * maxRows, displayOrderItems.length)
                            : displayOrderItems.length;

                        return (
                          <li key={i}>
                            <button
                              type="button"
                              onClick={() => downloadOrder(store.id, order.id, i + 1)}
                              className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium cursor-pointer"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                              {downloadInfo.file_count === 1
                                ? `${vendor.name} order`
                                : `Items ${start}-${end}`}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {downloadInfo.file_count > 1 && (
                      <p className="flex items-center gap-2">
                        <span>These files were downloaded as a ZIP archive.</span>
                        <button
                          type="button"
                          onClick={handleRedownload}
                          className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium cursor-pointer"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Re-download ZIP
                        </button>
                      </p>
                    )}
                    {downloadInfo.bulk_add_link !== '' && (
                      <p>
                        Upload {downloadInfo.file_count === 1 ? 'this file' : 'each file'} to{' '}
                        <a
                          href={downloadInfo.bulk_add_link}
                          target="_blank"
                          rel="noreferrer"
                          className="underline font-medium hover:text-blue-900"
                        >
                          {vendor.name}&apos;s {downloadInfo.bulk_add_label} page
                        </a>
                        .
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span>This order was submitted as a downloadable file.</span>
                    <button
                      type="button"
                      onClick={handleRedownload}
                      className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium cursor-pointer"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Re-download
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="pt-4 col-span-2 border-b border-b-gray-300" />
          </div>
          <PurchaseOrder
            store={store}
            vendor={vendor}
            order={order}
            orderItems={displayOrderItems}
          />
          {printing && (
            <PrintContainer onAfterPrint={() => setPrinting(false)}>
              <PurchaseOrder
                store={store}
                vendor={vendor}
                order={order}
                orderItems={displayOrderItems}
              />
            </PrintContainer>
          )}
        </div>
      </div>
      <Modal
        visible={duplicateConfirmationIsOpen}
        title="Are you sure you want to duplicate this order?"
        onClose={() => setDuplicateConfirmationIsOpen(false)}
        size="sm"
        footer={
          <>
            <SecondaryButton onClick={() => setDuplicateConfirmationIsOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={() => duplicateOrder()}>Duplicate</PrimaryButton>
          </>
        }
      >
        <div className="text-sm">
          <p className="pb-4">
            This order has already been sent to <span className="font-semibold">{vendor.name}</span>
            . You are responsible for canceling this order with{' '}
            <span className="font-semibold">{vendor.name}</span> to avoid duplicate shipments.
          </p>
          <p className="pb-4">
            Duplicating this order will create a new order with the same items and options.
          </p>
          <p>Do you want to continue?</p>
        </div>
      </Modal>
    </>
  );
}
